import { Css } from "@homebound/beam";
import type { ComponentProps, PropsWithChildren } from "react";
import { Link } from "react-router-dom";
import { createDesignPackagesUrl, createDesignPackageUrl } from "src/RouteUrls";
import { NextStepButton } from "src/components/stepper/useStepperWizard/NextStepButton";
import { disableBasedOnPotentialOperationText } from "src/routes/components/PotentialOperationsUtils";
import { PageHeader } from "src/routes/layout/PageHeader";
import { useNewDesignPackageContext } from "./NewDesignPackageContext";

type NewDesignPackageWizardPageProps = unknown;
/** Shows the page header and scrollable content for a design package wizard step. */
export function NewDesignPackageWizardPage(props: PropsWithChildren<NewDesignPackageWizardPageProps>) {
  const { children } = props;
  const { designPackage, isEditMode } = useNewDesignPackageContext();

  return (
    <>
      <PageHeader
        title={
          isEditMode
            ? `${designPackage?.canEdit.allowed ? "Edit" : "View"} Design Package`
            : "Create a new Design Package"
        }
        breadcrumb={[
          { label: "Design Packages", href: createDesignPackagesUrl() },
          { label: "Create a New Design Package", href: "" }, // href: "" does what?
        ]}
        xss={Css.px4.mb0.$}
      />
      <div /* fullpage, scrollable area, gray */ css={Css.df.fdc.aic.p4.w100.h100.oys.pbPx(215).bgGray100.$}>
        <div css={Css.df.fdc.aifs.gap3.hmaxc.maxwPx(800).$}>{children}</div>
      </div>
    </>
  );
}

/** Keeps the read-only explanation next to the step's action buttons on both wizard steps. */
export function NewDesignPackageNextStepButton(props: ComponentProps<typeof NextStepButton>) {
  return <NextStepButton {...props} beforeActions={<ReadOnlyNotice />} />;
}

/**
 * Explains why this version cannot be edited without obscuring the wizard content.
 *
 * The latest-version link keeps the wizard open so users do not need to find the cog again.
 */
function ReadOnlyNotice() {
  const { designPackage, editLatestVersion } = useNewDesignPackageContext();
  if (!designPackage) return null;

  const outdatedVersion = designPackage.aggregateLatest.id !== designPackage.version.id;
  const cannotEdit = disableBasedOnPotentialOperationText(designPackage.canEdit);
  if (!cannotEdit) return null;

  // The footer is a flex row; keep this note at its readable width instead of shrinking it word by word.
  return (
    <div data-testid="readOnlyNotice" css={Css.df.fdc.aife.gapPx(2).fs0.mr2.sm.wsnw.$}>
      <span css={Css.gray700.$}>{cannotEdit}</span>
      {outdatedVersion && (
        <Link
          data-testid="editLatestVersion"
          css={Css.smSb.$}
          to={createDesignPackageUrl(designPackage.id, designPackage.aggregateLatest.id)}
          onClick={(event) => {
            event.preventDefault();
            editLatestVersion();
          }}
        >
          Go to latest
        </Link>
      )}
    </div>
  );
}
